import { useMemo, useState } from 'react'
import { Plus, Trash } from 'lucide-react'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from '@univdiam/ui/components/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@univdiam/ui/components/table'
import { Button } from '@univdiam/ui/components/button'
import { Input } from '@univdiam/ui/components/input'
import { Label } from '@univdiam/ui/components/label'
import { Checkbox } from '@univdiam/ui/components/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@univdiam/ui/components/select'
import MediaUploader from '../MediaUploader'
import { toast } from 'sonner'

export type CatalogMetal = {
  code: string
  label: string
  tier: string
  color: string
}
export type CatalogDiamondType = { code: string; label: string; quality: string }
export type CatalogTier = { code: string; label: string }

export type CatalogModel = {
  metals: CatalogMetal[]
  diamondTypes: CatalogDiamondType[]
  tiers: CatalogTier[]
  jewelryTypes: string[]
}

type StoneOption = { id: string; size: string; carat_weight: string }

export type AddCatalogProductPayload = {
  sku: string
  title: string
  type_of_jewelry: string
  setting_style: string
  diamond_count: string
  dimensions: string
  item_weight: string
  other: string
  iframe_link: string
  stoneOptions: { size: string; carat_weight: string }[]
  diamondTypes: string[]
  metals: string[]
  prices: Record<string, string>
  media: Record<string, string[]>
}

type Props = {
  model: CatalogModel
  onSubmit: (payload: AddCatalogProductPayload) => Promise<void>
  handleSendToUpload: (files: File[]) => Promise<string[]>
  mediaLoading?: boolean
  isSubmitting?: boolean
}

const newOption = (): StoneOption => ({
  id: String(Date.now() + Math.random()),
  size: '',
  carat_weight: ''
})

const priceKey = (optionIndex: number, typeCode: string, tier: string) =>
  `${optionIndex}|${typeCode}|${tier}`
const mediaKey = (size: string, color: string) => `${size}|${color}`

export default function AddCatalogProduct({
  model,
  onSubmit,
  handleSendToUpload,
  mediaLoading = false,
  isSubmitting = false
}: Props) {
  const [sku, setSku] = useState('')
  const [title, setTitle] = useState('')
  const [typeOfJewelry, setTypeOfJewelry] = useState(model.jewelryTypes[0] || '')
  const [settingStyle, setSettingStyle] = useState('Prong')
  const [diamondCount, setDiamondCount] = useState('')
  const [dimensions, setDimensions] = useState('')
  const [iframeLink, setIframeLink] = useState('')
  const [itemWeight, setItemWeight] = useState('')
  const [other, setOther] = useState('')

  const [options, setOptions] = useState<StoneOption[]>([newOption()])
  const [diamondTypes, setDiamondTypes] = useState<string[]>(['LG'])
  const [metals, setMetals] = useState<string[]>(model.metals.map((m) => m.code))
  const [prices, setPrices] = useState<Record<string, string>>({})
  const [media, setMedia] = useState<Record<string, string[]>>({})

  const selectedMetals = model.metals.filter((m) => metals.includes(m.code))
  const selectedTypes = model.diamondTypes.filter((t) =>
    diamondTypes.includes(t.code)
  )

  // Prices are per karat tier: 14k white, yellow and rose are always the same
  // number in the sheet, likewise 18k, with platinum on its own.
  const activeTiers = useMemo(
    () => model.tiers.filter((t) => selectedMetals.some((m) => m.tier === t.code)),
    [model.tiers, selectedMetals]
  )

  // Photos are shot per size and colour and shared by every karat in that
  // colour -- platinum reuses the white set.
  const activeColors = useMemo(
    () => [...new Set(selectedMetals.map((m) => m.color))],
    [selectedMetals]
  )
  const sizes = useMemo(
    () => [...new Set(options.map((o) => o.size.trim()).filter(Boolean))],
    [options]
  )

  const variantCount = options.length * selectedTypes.length * selectedMetals.length

  const toggle = (list: string[], code: string) =>
    list.includes(code) ? list.filter((c) => c !== code) : [...list, code]

  const updateOption = (id: string, patch: Partial<StoneOption>) =>
    setOptions((rows) =>
      rows.map((row) => (row.id === id ? { ...row, ...patch } : row))
    )

  const handleUpload = async (key: string, files: File[]) => {
    const urls = await handleSendToUpload(files)
    setMedia((m) => ({ ...m, [key]: [...(m[key] || []), ...urls] }))
  }

  const handleSubmit = async () => {
    if (!sku.trim()) return toast.error('Parent SKU is required')
    if (!title.trim()) return toast.error('Title is required')
    if (options.some((o) => !o.size.trim()))
      return toast.error('Every stone option needs a size')
    if (selectedTypes.length === 0)
      return toast.error('Select at least one diamond type')
    if (selectedMetals.length === 0)
      return toast.error('Select at least one metal')

    for (let i = 0; i < options.length; i++) {
      for (const type of selectedTypes) {
        for (const tier of activeTiers) {
          if (!prices[priceKey(i, type.code, tier.code)]) {
            return toast.error(
              `Missing ${tier.label} price for ${options[i]!.size}mm ${type.label}`
            )
          }
        }
      }
    }

    await onSubmit({
      sku: sku.trim(),
      title: title.trim(),
      type_of_jewelry: typeOfJewelry,
      setting_style: settingStyle,
      diamond_count: diamondCount,
      dimensions,
      item_weight: itemWeight,
      other,
      iframe_link: iframeLink,
      stoneOptions: options.map((o) => ({
        size: o.size.trim(),
        carat_weight: o.carat_weight
      })),
      diamondTypes: selectedTypes.map((t) => t.code),
      metals: selectedMetals.map((m) => m.code),
      prices,
      media
    })
  }

  return (
    <div className="space-y-4 py-4">
      <Card className="shadow-xs">
        <CardHeader>
          <CardTitle className="text-body1">Product</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Parent SKU *</Label>
            <Input
              placeholder="e.g. SR148"
              value={sku}
              onChange={(e) => setSku(e.target.value.toUpperCase())}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Title *</Label>
            <Input
              placeholder="e.g. SR148 Solitaire Ring"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Type of jewelry</Label>
            <Select value={typeOfJewelry} onValueChange={setTypeOfJewelry}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {model.jewelryTypes.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Setting style</Label>
            <Input
              value={settingStyle}
              onChange={(e) => setSettingStyle(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Diamond count</Label>
            <Input
              type="number"
              placeholder="e.g. 11"
              value={diamondCount}
              onChange={(e) => setDiamondCount(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Item weight</Label>
            <Input
              value={itemWeight}
              onChange={(e) => setItemWeight(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Dimensions</Label>
            <Input
              value={dimensions}
              onChange={(e) => setDimensions(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Other</Label>
            <Input value={other} onChange={(e) => setOther(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>3D link</Label>
            <Input
              type="url"
              placeholder="https://ijewel3d.com/..."
              value={iframeLink}
              onChange={(e) => setIframeLink(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-xs">
        <CardHeader>
          <CardTitle className="text-body1">Stone options</CardTitle>
          <p className="text-body2 text-muted-foreground">
            One row per head size and centre carat you sell. SR148 is five sizes,
            one carat each; ARCH is one 2.8mm head in six carats.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Head size (mm) *</TableHead>
                <TableHead>Centre carat</TableHead>
                <TableHead className="w-16 text-center">Remove</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {options.map((option) => (
                <TableRow key={option.id}>
                  <TableCell>
                    <Input
                      placeholder="2.9"
                      value={option.size}
                      onChange={(e) =>
                        updateOption(option.id, { size: e.target.value })
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0.50"
                      value={option.carat_weight}
                      onChange={(e) =>
                        updateOption(option.id, { carat_weight: e.target.value })
                      }
                    />
                  </TableCell>
                  <TableCell className="text-center">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label="Remove stone option"
                      disabled={options.length === 1}
                      onClick={() =>
                        setOptions((rows) => rows.filter((r) => r.id !== option.id))
                      }
                    >
                      <Trash className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Button
            type="button"
            variant="outline"
            onClick={() => setOptions((rows) => [...rows, newOption()])}
          >
            <Plus size={16} /> Add stone option
          </Button>
        </CardContent>
      </Card>

      <Card className="shadow-xs">
        <CardHeader>
          <CardTitle className="text-body1">Diamond types &amp; metals</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>Diamond types</Label>
            {model.diamondTypes.map((t) => (
              <label
                key={t.code}
                className="flex cursor-pointer items-center gap-2 text-body2"
              >
                <Checkbox
                  checked={diamondTypes.includes(t.code)}
                  onCheckedChange={() =>
                    setDiamondTypes((list) => toggle(list, t.code))
                  }
                />
                {t.label}
                <span className="text-muted-foreground">({t.quality})</span>
              </label>
            ))}
          </div>
          <div className="space-y-2">
            <Label>Metals</Label>
            {model.metals.map((m) => (
              <label
                key={m.code}
                className="flex cursor-pointer items-center gap-2 text-body2"
              >
                <Checkbox
                  checked={metals.includes(m.code)}
                  onCheckedChange={() => setMetals((list) => toggle(list, m.code))}
                />
                {m.label}
              </label>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-xs">
        <CardHeader>
          <CardTitle className="text-body1">Pricing</CardTitle>
          <p className="text-body2 text-muted-foreground">
            One price per karat tier — white, yellow and rose of the same karat
            always cost the same.
          </p>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Stone option</TableHead>
                <TableHead>Diamond type</TableHead>
                {activeTiers.map((tier) => (
                  <TableHead key={tier.code}>{tier.label} price *</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {options.flatMap((option, i) =>
                selectedTypes.map((type) => (
                  <TableRow key={`${option.id}-${type.code}`}>
                    <TableCell className="whitespace-nowrap">
                      {option.size || '—'}mm
                      {option.carat_weight ? ` · ${option.carat_weight} ct` : ''}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-muted-foreground">
                      {type.quality}
                    </TableCell>
                    {activeTiers.map((tier) => (
                      <TableCell key={tier.code}>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="1225"
                          value={prices[priceKey(i, type.code, tier.code)] || ''}
                          onChange={(e) =>
                            setPrices((p) => ({
                              ...p,
                              [priceKey(i, type.code, tier.code)]: e.target.value
                            }))
                          }
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className="shadow-xs">
        <CardHeader>
          <CardTitle className="text-body1">Photos &amp; video</CardTitle>
          <p className="text-body2 text-muted-foreground">
            One set per size and colour. Every karat of that colour uses it —
            platinum shows the white set.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {sizes.length === 0 ? (
            <p className="text-body2 text-muted-foreground">
              Add a stone option size first.
            </p>
          ) : (
            sizes.map((size) =>
              activeColors.map((color) => {
                const key = mediaKey(size, color)
                return (
                  <div key={key} className="space-y-1.5">
                    <Label className="capitalize">
                      {size}mm · {color}
                    </Label>
                    <MediaUploader
                      value={media[key] || []}
                      mediaLoading={mediaLoading}
                      handleSendToUpload={(files) => handleUpload(key, files)}
                      onRemove={(index) =>
                        setMedia((m) => ({
                          ...m,
                          [key]: (m[key] || []).filter((_, i) => i !== index)
                        }))
                      }
                    />
                  </div>
                )
              })
            )
          )}
        </CardContent>
      </Card>

      <div className="sticky bottom-0 flex items-center justify-between gap-3 border-t border-border bg-background py-3">
        <p className="text-body2 text-muted-foreground">
          Creates{' '}
          <span className="font-medium text-foreground">{variantCount}</span>{' '}
          variants
          {options.length > 0 && selectedTypes.length > 0 && selectedMetals.length > 0
            ? ` (${options.length} × ${selectedTypes.length} × ${selectedMetals.length})`
            : ''}
        </p>
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitting || variantCount === 0}
        >
          {isSubmitting ? 'Creating…' : `Create ${variantCount} variants`}
        </Button>
      </div>
    </div>
  )
}
